import React, { useState } from 'react';
import { Button, useTheme, alpha, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';

const EasyOPSwitchButton = ({ isScrolled, variant = 'desktop' }) => {
  const { user, isAuthenticated } = useBookMyOpAuth();
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  // Only render if user is authenticated
  if (!isAuthenticated) {
    return null;
  }

  const isEasyOPPatient = user?.is_easyop_patient === true;

  const handleClick = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  // SVG Icon component
  const EasyOPIcon = ({ iconColor }) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="20" 
      height="20" 
      viewBox="0 0 256 256" 
      fill="none"
      style={{ marginRight: '8px' }}
    >
      <path 
        d="M 128.945 0 C 199.203 0.508 256 57.617 256 127.994 C 256 198.686 198.692 255.994 128 255.994 C 57.308 255.994 0 198.686 0 127.994 C 0 57.617 56.797 0.509 127.054 0 C 87.725 0.506 56 32.544 56 71.994 C 56 111.759 88.236 143.994 128 143.994 C 167.764 143.994 200 111.759 200 71.994 C 200 32.545 168.274 0.506 128.945 0 Z M 128 47.994 C 141.255 47.994 152 58.739 152 71.994 C 152 85.249 141.255 95.994 128 95.994 C 114.745 95.994 104 85.249 104 71.994 C 104 58.739 114.745 47.994 128 47.994 Z" 
        fill={iconColor || (isScrolled ? theme.palette.primary.main : 'white')}
      />
    </svg>
  );

  // Mobile variant styling
  if (variant === 'mobile') {
    return (
      <>
        <Button
          onClick={handleClick}
          variant="contained"
          startIcon={<EasyOPIcon iconColor={theme.palette.primary.main} />}
          sx={{
            borderRadius: '12px',
            fontWeight: 600,
            px: { xs: 1, sm: 1.5 },
            py: 0.5,
            fontSize: { xs: '0.7rem', sm: '0.8rem' },
            textTransform: 'none',
            background: 'rgba(255, 255, 255, 0.95)',
            color: theme.palette.primary.main,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              background: 'white',
              borderColor: theme.palette.primary.main,
            },
            '& .MuiButton-startIcon': {
              marginRight: '4px',
            }
          }}
        >
          Easy OP
        </Button>

        {/* Confirmation Dialog - Show different content based on is_easyop_patient */}
        <Dialog
          open={openDialog}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              minWidth: { xs: '90%', sm: '400px' },
              maxWidth: '500px'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 600,
            fontSize: '1.25rem',
            pb: 1
          }}>
            Easy OP is coming soon
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ 
              fontSize: '0.95rem',
              color: 'text.primary',
              mb: 1
            }}>
              Easy OP membership will let you book OPs with top hospitals and doctors. This feature is coming soon.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ 
            px: 3,
            pb: 2.5,
            pt: 1,
            gap: 1
          }}>
            <Button
              onClick={handleClose}
              variant="contained"
              sx={{
                borderRadius: '12px',
                fontWeight: 600,
                px: 2.5,
                py: 0.75,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Okay
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Desktop variant styling (matches Navbar button style)
  return (
    <>
      <Button
        onClick={handleClick}
        startIcon={<EasyOPIcon />}
        sx={{
          color: isScrolled 
            ? theme.palette.primary.main
            : 'white',
          borderRadius: '12px',
          fontWeight: 600,
          px: 1.5,
          py: 0.5,
          fontSize: '0.85rem',
          textTransform: 'none',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          background: isScrolled 
            ? alpha(theme.palette.primary.main, 0.1)
            : 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: isScrolled
            ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            : '1px solid rgba(255, 255, 255, 0.2)',
          '& .MuiButton-startIcon': {
            color: isScrolled 
              ? theme.palette.primary.main
              : 'white',
            transition: 'all 0.3s ease',
            marginRight: '4px',
            '& svg': {
              fontSize: '1rem',
            }
          },
          '&:hover': {
            background: isScrolled 
              ? alpha(theme.palette.primary.main, 0.15)
              : 'rgba(255, 255, 255, 0.2)',
            transform: 'translateY(-1px)',
            borderColor: isScrolled
              ? theme.palette.primary.main
              : 'rgba(255, 255, 255, 0.4)',
            '& .MuiButton-startIcon': {
              transform: 'scale(1.1)',
            }
          }
        }}
      >
        Switch to Easy OP
      </Button>

      {/* Confirmation Dialog - Show different content based on is_easyop_patient */}
      <Dialog
        open={openDialog}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            minWidth: { xs: '90%', sm: '400px' },
            maxWidth: '500px'
          }
        }}
      >
          <DialogTitle sx={{ 
            fontWeight: 600,
            fontSize: '1.25rem',
            pb: 1
          }}>
            Easy OP is coming soon
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ 
              fontSize: '0.95rem',
              color: 'text.primary',
              mb: 1
            }}>
              Easy OP membership will let you book OPs with top hospitals and doctors. This feature is coming soon.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ 
            px: 3,
            pb: 2.5,
            pt: 1,
            gap: 1
          }}>
            <Button
              onClick={handleClose}
              variant="contained"
              sx={{
                borderRadius: '12px',
                fontWeight: 600,
                px: 2.5,
                py: 0.75,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Okay
            </Button>
          </DialogActions>
      </Dialog>
    </>
  );
};

export default EasyOPSwitchButton;

